import { useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { useBioPageAutosave } from '@/hooks/useBioPageAutosave'
import { BioLinkEditor, type BioLink } from '@/components/bio/BioLinkEditor'
import { BioProductEditor, type BioProduct } from '@/components/bio/BioProductEditor'
import { BioAppearanceEditor } from '@/components/bio/BioAppearanceEditor'
import { BioSettingsEditor } from '@/components/bio/BioSettingsEditor'
import { BioPhonePreview } from '@/components/bio/BioPhonePreview'
import { BioAnalytics } from '@/components/bio/BioAnalytics'

interface BioPage {
  id: string
  slug: string
  title: string
  bio: string | null
  profile_image_url: string | null
  display_name: string | null
  handle: string | null
  sticky_header: boolean
  theme: string
  background_type: string
  background_value: string | null
  button_style: string
  button_color: string
  text_color: string
  font_family: string
  seo_title: string | null
  seo_description: string | null
  is_published: boolean
  custom_domain: string | null
  view_count: number
  default_utm_source: string | null
  default_utm_medium: string | null
  default_utm_campaign: string | null
  default_referral_id: string | null
  password_hash: string | null
  meta_pixel_id: string | null
  ga_measurement_id: string | null
  tiktok_pixel_id: string | null
}

type Tab = 'links' | 'products' | 'appearance' | 'settings' | 'analytics'

const NAV_ITEMS: { key: Tab; label: string; icon: string }[] = [
  { key: 'links', label: 'Links', icon: 'link' },
  { key: 'products', label: 'Products', icon: 'storefront' },
  { key: 'appearance', label: 'Appearance', icon: 'palette' },
  { key: 'settings', label: 'Settings', icon: 'manage_accounts' },
  { key: 'analytics', label: 'Analytics', icon: 'analytics' },
]


export function BioPageEditor() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<Tab>('links')
  const { save, status: saveStatus } = useBioPageAutosave(id)
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)

  const { data: pageData, isLoading: pageLoading } = useQuery<{ success: boolean; data: BioPage }>({
    queryKey: ['bio-page', id],
    queryFn: () => apiRequest(`/api/bio-pages/${id}`),
    enabled: !!id,
  })

  const { data: linksData } = useQuery<{ success: boolean; data: BioLink[] }>({
    queryKey: ['bio-links', id],
    queryFn: () => apiRequest(`/api/bio-pages/${id}/links`),
    enabled: !!id,
  })

  const { data: productsData } = useQuery<{ success: boolean; data: BioProduct[] }>({
    queryKey: ['bio-products', id],
    queryFn: () => apiRequest(`/api/bio-pages/${id}/products`),
    enabled: !!id,
  })

  const page = pageData?.data
  const links = linksData?.data || []
  const products = productsData?.data || []

  const handleChange = useCallback((updates: Partial<BioPage>) => {
    if (!page) return
    queryClient.setQueryData(['bio-page', id], {
      success: true,
      data: { ...page, ...updates },
    })
    save(updates)
  }, [page, id, save, queryClient])

  const handleCopyUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [])

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-[#2B6CEE] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!page) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 dark:text-gray-400 mb-4">Bio page not found</p>
        <Link to="/bio-pages" className="text-[#2B6CEE] hover:underline text-sm">Back to Bio Pages</Link>
      </div>
    )
  }

  const baseUrl = window.location.origin
  const publicUrl = `${baseUrl}/@${page.slug}`
  const displayName = page.display_name || page.title
  const initials = displayName
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
  return (
    <div
      className="-mx-4 sm:-mx-8 -mt-4 sm:-mt-8 flex flex-col"
      style={{ height: 'calc(100vh - 65px)' }}
    >
      {/* TOP BAR */}
      <div className="h-16 border-b border-[#e7ebf3] dark:border-white/10 bg-white dark:bg-[#0d121b] flex items-center px-6 justify-between shrink-0 gap-4">
        {/* Left: back + title */}
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/bio-pages"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 transition-colors"
            title="Back to Bio Pages"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          </Link>
          <div className="w-px h-5 bg-[#e7ebf3] dark:bg-white/10" />
          <span className="text-sm font-semibold text-[#0d121b] dark:text-white whitespace-nowrap">
            Bio Page Editor
          </span>
        </div>

        {/* Center: URL pill */}
        <div className="flex-1 flex justify-center min-w-0">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 dark:bg-white/5 border border-[#e7ebf3] dark:border-white/10 max-w-xs w-full">
            <span className="material-symbols-outlined text-[14px] text-gray-400 shrink-0">link</span>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-[#2B6CEE] truncate flex-1"
            >
              {publicUrl}
            </a>
            <button
              onClick={() => handleCopyUrl(publicUrl)}
              className="shrink-0 text-gray-400 hover:text-[#2B6CEE] transition-colors"
              title="Copy URL"
            >
              <span className="material-symbols-outlined text-[14px]">
                {copied ? 'check' : 'content_copy'}
              </span>
            </button>
          </div>
        </div>

        {/* Right: save status + actions */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Save status */}
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            {saveStatus === 'saving' && (
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
            )}
            {saveStatus === 'saved' && (
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
            )}
            {saveStatus === 'error' && (
              <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
            )}
            <span className="hidden md:block">
              {saveStatus === 'saving' ? 'Saving...' :
                saveStatus === 'saved' ? 'Saved' :
                  saveStatus === 'error' ? 'Error' : ''}
            </span>
          </div>

          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#2B6CEE] border border-[#2B6CEE]/30 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            <span className="material-symbols-outlined text-[14px]">open_in_new</span>
            View Live
          </a>

          <button
            onClick={() => save({})}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#2B6CEE] rounded-lg hover:bg-[#2454c7] transition-colors shadow-sm shadow-blue-500/20"
          >
            <span className="material-symbols-outlined text-[14px]">save</span>
            <span className="hidden sm:block">Save Changes</span>
          </button>
        </div>
      </div>

      {/* 3-PANEL ROW */}
      <div className="flex flex-1 overflow-hidden">

        {/* LEFT SIDEBAR */}
        <aside className="w-60 border-r border-[#e7ebf3] dark:border-white/10 bg-white dark:bg-[#0d121b] overflow-y-auto flex flex-col shrink-0">
          {/* Profile section */}
          <div className="flex flex-col items-center text-center px-5 pt-4 pb-3 border-b border-[#e7ebf3] dark:border-white/10">
            {page.profile_image_url ? (
              <img
                src={page.profile_image_url}
                alt={displayName}
                className="w-13 h-13 rounded-full object-cover mb-3 ring-2 ring-[#e7ebf3] dark:ring-white/10"
                style={{ width: 52, height: 52 }}
              />
            ) : (
              <div
                className="rounded-full bg-[#2B6CEE]/10 flex items-center justify-center mb-3 shrink-0"
                style={{ width: 52, height: 52 }}
              >
                <span className="text-base font-bold text-[#2B6CEE]">{initials}</span>
              </div>
            )}
            <p className="text-sm font-semibold text-[#0d121b] dark:text-white leading-tight truncate w-full">
              {displayName}
            </p>
            <p className="text-xs text-[#4c669a] mt-0.5 truncate w-full">
              @{page.slug}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex flex-col gap-0.5 px-3 pt-2 pb-3 flex-1">
            {NAV_ITEMS.map(item => {
              const isActive = activeTab === item.key
              return (
                <button
                  key={item.key}
                  onClick={() => setActiveTab(item.key)}
                  className={`w-full h-10 rounded-lg px-2.5 flex items-center gap-2.5 text-[13px] transition-colors ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-[#2B6CEE] font-semibold'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  <span className={`material-symbols-outlined text-[18px] ${isActive ? 'text-[#2B6CEE]' : 'text-gray-400 dark:text-gray-500'}`}>
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              )
            })}
          </nav>
        </aside>

        {/* CENTER PANEL */}
        <main className="flex-1 bg-[#f6f6f8] dark:bg-[#101622] overflow-y-auto flex flex-col min-w-0">
          {/* Content area */}
          <div className="p-8 flex-1">
            {activeTab === 'links' && (
              <BioLinkEditor pageId={page.id} links={links} />
            )}
            {activeTab === 'products' && (
              <BioProductEditor pageId={page.id} products={products} />
            )}
            {activeTab === 'appearance' && (
              <BioAppearanceEditor
                data={{
                  theme: page.theme,
                  background_type: page.background_type,
                  background_value: page.background_value,
                  button_style: page.button_style,
                  button_color: page.button_color,
                  text_color: page.text_color,
                  font_family: page.font_family,
                }}
                onChange={handleChange}
              />
            )}
            {activeTab === 'settings' && (
              <BioSettingsEditor
                pageId={page.id}
                data={{
                  slug: page.slug,
                  title: page.title,
                  bio: page.bio,
                  profile_image_url: page.profile_image_url,
                  display_name: page.display_name,
                  handle: page.handle,
                  sticky_header: page.sticky_header,
                  seo_title: page.seo_title,
                  seo_description: page.seo_description,
                  is_published: page.is_published,
                  custom_domain: page.custom_domain,
                  default_utm_source: page.default_utm_source,
                  default_utm_medium: page.default_utm_medium,
                  default_utm_campaign: page.default_utm_campaign,
                  default_referral_id: page.default_referral_id,
                  password_hash: page.password_hash,
                  meta_pixel_id: page.meta_pixel_id,
                  ga_measurement_id: page.ga_measurement_id,
                  tiktok_pixel_id: page.tiktok_pixel_id,
                }}
                onChange={handleChange}
              />
            )}
            {activeTab === 'analytics' && (
              <BioAnalytics pageId={page.id} />
            )}
          </div>
        </main>

        {/* RIGHT PANEL - phone preview */}
        <aside className="hidden xl:flex w-[380px] border-l border-[#e7ebf3] dark:border-white/10 bg-white dark:bg-[#0d121b] flex-col shrink-0">
          {/* Preview header */}
          <div className="h-14 border-b border-[#e7ebf3] dark:border-white/10 flex items-center justify-between px-5 shrink-0">
            <span className="text-sm font-semibold text-[#0d121b] dark:text-white">Live Preview</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 rounded-full">
                <span className="material-symbols-outlined text-[12px] text-green-600">visibility</span>
                <span className="text-[11px] font-medium text-green-600">{page.view_count}</span>
              </div>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 border border-[#e7ebf3] dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                <span className="material-symbols-outlined text-[13px]">share</span>
                Share
              </a>
            </div>
          </div>

          {/* Phone preview — BioPhonePreview owns its frame */}
          <div className="flex-1 flex items-center justify-center overflow-y-auto py-6">
            <BioPhonePreview
                      page={{
                        title: page.title,
                        bio: page.bio,
                        profile_image_url: page.profile_image_url,
                        display_name: page.display_name,
                        handle: page.handle,
                        theme: page.theme,
                        background_type: page.background_type,
                        background_value: page.background_value,
                        button_style: page.button_style,
                        button_color: page.button_color,
                        text_color: page.text_color,
                        font_family: page.font_family,
                      }}
                      links={links}
                      products={products}
                    />
          </div>
        </aside>

      </div>
    </div>
  )
}
